<template>
  <b-table
    ref="btable"
    :data="data"
    :sticky-header="stickyHeader"
    :scrollable="scrollable"
    :hoverable="hoverable"
    :loading="loading"
    :paginated="paginated"
    :backend-pagination="backendPagination"
    :backend-sorting="backendSorting"
    :custom-row-key="customRowKey"
    :total="total"
    :icon-pack="iconPack"
    :sort-icon="sortIcon"
    :per-page="perPage"
    :checkable="checkable"
    :header-checkable="headerCheckable"
    :custom-is-checked="customIsChecked"
    :checked-rows="checkedRows"
    :is-row-checkable="isRowCheckable"
    :current-page="currentPage"
    :default-sort-direction="defaultSortDirection"
    :default-sort="defaultSort"
    :height="height"
    :is-row-selectable="isRowSelectable"
    :selected.sync="selectedInternal"
    :row-class="rowClass"
    :detailed="detailed"
    :show-detail-icon="showDetailIcon"
    :show-header="showHeader"
    :detail-key="detailKey"
    :mobile-cards="mobileCards"
    :detail-icon="detailIcon"
    @page-change="onPageChange"
    @sort="onSort"
    @click="onClick"
    @check="onCheck"
    @update:checkedRows="onCheckedRowsUpdate"
    @update:checked-rows="onCheckedRowsUpdate"
  >
    <!--
      Buefy v3's <b-checkbox> emits `update:modelValue` (not `change`).
      b-table's built-in header checkbox uses `@change="checkAll"`, which can break "check/uncheck all" under Vue 3.
      This slot provides a compatible fallback wiring `@update:modelValue` to `checkAll`.
    -->
    <template #check-all="{ isAllChecked, isAllUncheckable, checkAll }">
      <slot
        name="check-all"
        :is-all-checked="isAllChecked"
        :is-all-uncheckable="isAllUncheckable"
        :check-all="checkAll"
      >
        <b-checkbox
          autocomplete="off"
          :model-value="isAllChecked"
          :disabled="isAllUncheckable"
          @update:modelValue="checkAll"
        />
      </slot>
    </template>
    <slot />
    <template #detail="{ row, index }">
      <slot name="detail" :row="row" :index="index" />
    </template>
    <template #empty>
      <slot name="empty" />
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </b-table>
</template>

<script>
export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'TableView',
  emits: ['page-change', 'sort', 'click', 'check', 'update:checkedRows', 'update:checked-rows'],
  slotsDefinition: {
    checkAll: { isAllChecked: Boolean, isAllUncheckable: Boolean, checkAll: Function },
    detail: { row: Object, index: Number },
    empty: {},
    footer: {}
  },
  props: {
    data: {
      type: Array
    },
    stickyHeader: {
      type: Boolean,
      default: false
    },
    scrollable: {
      type: Boolean,
      default: false
    },
    hoverable: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    paginated: {
      type: Boolean,
      default: false
    },
    backendPagination: {
      type: Boolean,
      default: false
    },
    backendSorting: {
      type: Boolean,
      default: false
    },
    customRowKey: {
      type: String
    },
    total: {
      type: [Number, String],
      default: 0
    },
    iconPack: {
      type: String
    },
    sortIcon: {
      type: String,
      default: 'arrow-up'
    },
    perPage: {
      type: Number,
      default: 20
    },
    checkable: {
      type: Boolean,
      default: false
    },
    headerCheckable: {
      type: Boolean,
      default: true
    },
    customIsChecked: {
      type: Function
    },
    checkedRows: {
      type: Array,
      default: () => []
    },
    isRowCheckable: {
      type: Function
    },
    currentPage: {
      type: Number,
      default: 1
    },
    defaultSortDirection: {
      type: String,
      default: 'asc'
    },
    defaultSort: {
      type: [String, Array]
    },
    height: {
      type: [Number, String]
    },
    isRowSelectable: {
      type: Function,
      default: () => true
    },
    selected: {
      type: Object
    },
    rowClass: {
      type: Function,
      default: () => ''
    },
    detailed: {
      type: Boolean,
      default: false
    },
    showDetailIcon: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    detailKey: {
      type: String,
      default: ''
    },
    mobileCards: {
      type: Boolean,
      default: true
    },
    detailIcon: {
      type: String,
      default: 'chevron-right'
    }
  },
  data() {
    return {
      selectedInternal: this.selected,
      checkedRowsInternal: this.checkedRows,
      receivedCheckInTick: false,
      checkboxClickCaptureHandler: null
    };
  },
  watch: {
    checkedRows(rows) {
      this.checkedRowsInternal = rows ?? [];
    }
  },
  methods: {
    // Vue 3 compat + Buefy v3 mismatch:
    // - b-table row checkboxes call `checkRow(...)` via a `@click` listener on <b-checkbox>.
    // - <b-checkbox> is a component and does not reliably forward DOM click events in compat mode.
    // Result: row checkbox visually toggles, but b-table never updates `checkedRows` / emits events.
    //
    // This capture-phase listener detects row-checkbox clicks and calls `checkRow(...)` ourselves.
    attachRowCheckboxClickWorkaround() {
      if (this.checkboxClickCaptureHandler) return;

      this.checkboxClickCaptureHandler = (event) => {
        if (!this.checkable) return;

        const table = this.$refs.btable;
        if (!table?.checkRow) return;

        const target = event?.target;
        if (!(target instanceof Element)) return;

        // Ignore header checkbox clicks (handled by our `check-all` slot fallback).
        if (target.closest('thead')) return;

        const checkboxCell = target.closest('td.checkbox-cell');
        if (!checkboxCell) return;

        const rowEl = target.closest('tbody tr');
        if (!rowEl || rowEl.classList.contains('detail')) return;

        const tbody = rowEl.closest('tbody');
        if (!tbody) return;

        const rowEls = Array.from(tbody.querySelectorAll('tr')).filter(
          (tr) => !tr.classList.contains('detail')
        );
        const index = rowEls.indexOf(rowEl);
        if (index < 0) return;

        const visibleData = table.visibleData ?? this.data ?? [];
        const row = Array.isArray(visibleData) ? visibleData[index] : null;
        if (!row) return;

        // Prevent double-toggles when both our workaround and Buefy's handler fire.
        event.preventDefault();
        event.stopPropagation();
        table.checkRow(row, index, event);
      };

      this.$el?.addEventListener('click', this.checkboxClickCaptureHandler, true);
    },
    // Keep teardown explicit so we don't leak capture listeners across route changes/unmounts.
    detachRowCheckboxClickWorkaround() {
      if (!this.checkboxClickCaptureHandler) return;
      this.$el?.removeEventListener('click', this.checkboxClickCaptureHandler, true);
      this.checkboxClickCaptureHandler = null;
    },
    toggleDetails(row) {
      this.$refs.btable.toggleDetails(row);
    },
    isVisibleDetailRow(row) {
      return this.$refs.btable.isVisibleDetailRow(row);
    },
    onPageChange(...args) {
      this.$emit('page-change', ...args);
    },
    onSort(...args) {
      this.$emit('sort', ...args);
    },
    onClick(...args) {
      this.$emit('click', ...args);
    },
    onCheck(...args) {
      // Guard used to avoid re-emitting `check` when `update:checkedRows` also comes in.
      this.receivedCheckInTick = true;
      this.$nextTick(() => {
        this.receivedCheckInTick = false;
      });
      this.$emit('check', ...args);
    },
    onCheckedRowsUpdate(checkedRows) {
      // b-table v3 uses `update:checkedRows` (camelCase). Some consumers still listen to the
      // legacy `update:checked-rows` (kebab-case), so we forward both.
      const prevRows = this.checkedRowsInternal ?? [];
      const nextRows = checkedRows ?? [];
      this.checkedRowsInternal = nextRows;

      this.$emit('update:checkedRows', nextRows);
      this.$emit('update:checked-rows', nextRows);

      if (!this.receivedCheckInTick) {
        const toggledRow = this.findToggledRow(prevRows, nextRows);
        this.$emit('check', nextRows, toggledRow);
      }
    },
    findToggledRow(prevRows, nextRows) {
      const eq = (a, b) => this.rowEquals(a, b);

      const added = nextRows.find((r) => !prevRows.some((p) => eq(p, r)));
      if (added) return added;

      const removed = prevRows.find((r) => !nextRows.some((n) => eq(n, r)));
      return removed;
    },
    rowEquals(a, b) {
      if (a === b) return true;
      if (!a || !b) return false;
      if (typeof this.customIsChecked === 'function') {
        return !!this.customIsChecked(a, b);
      }
      if (this.customRowKey) {
        return a[this.customRowKey] === b[this.customRowKey];
      }
      return false;
    }
  },
  mounted() {
    this.attachRowCheckboxClickWorkaround();
  },
  beforeUnmount() {
    this.detachRowCheckboxClickWorkaround();
  }
};
</script>
